import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Camera, Warning, X } from 'shared/icons';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import FileUploader from '../../components/ui/FileUploader';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Select from '../../components/ui/Select';
import StandardTable from '../../components/ui/StandardTable';
import Textarea from '../../components/ui/Textarea';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';
import { ResolveAssetUrl } from '../../utils/assetUrl';
import { FormatDateTimeBr } from '../../utils/date';
import { FormatFileSize } from '../../utils/format';
import { WMS_STATUS_CONFIG } from '../../utils/status';

const flowDefault = ['pending', 'idle', 'picking', 'ready_to_collect', 'collected'];
const flowRejected = ['pending', 'rejected'];
const TERMINAL_STATUSES = new Set(['collected', 'rejected']);
const MAX_PHOTOS = 10;
const MIN_PHOTOS_TO_COMPLETE = 4;
const MAX_COLLECTION_VIDEOS = 30;
const MAX_VIDEO_MB = 500;
const MAX_VIDEO_BYTES = MAX_VIDEO_MB * 1024 * 1024;
const MAX_EXTRA_FILES = 20;

function toDate(value) {
    if (!value) {
        return null;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date;
}

function formatDuration(seconds) {
    const totalSeconds = Number(seconds || 0);
    if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
        return '-';
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const remainingSeconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
        return `${minutes}m ${remainingSeconds}s`;
    }

    return `${remainingSeconds}s`;
}

function resolveFileType(file = {}) {
    const mime = String(file?.mime_type || '').toLowerCase();
    const url = String(file?.url || '').toLowerCase();
    if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|heic)$/i.test(url)) {
        return 'image';
    }
    if (mime.startsWith('video/') || /\.(mp4|mov|avi|wmv|mkv|webm|m4v)$/i.test(url)) {
        return 'video';
    }
    if (mime.includes('pdf') || /\.pdf$/i.test(url)) {
        return 'pdf';
    }
    return 'other';
}

function resolveFileFormat(file = {}) {
    const mime = String(file?.mime_type || '')
        .trim()
        .toLowerCase();
    if (mime.includes('pdf')) {
        return 'PDF';
    }
    if (mime.startsWith('image/')) {
        const imageFormat = mime.split('/')[1];
        return imageFormat ? imageFormat.toUpperCase() : 'IMAGE';
    }
    if (mime) {
        return mime.toUpperCase();
    }

    const url = String(file?.url || '').trim();
    const extension = url.split('.').pop();
    return extension ? extension.toUpperCase() : '-';
}

function resolveNormalizedFileLabel(file = {}) {
    const rawLabel = String(file?.label || '')
        .trim()
        .toLowerCase();
    if (['romaneio', 'separation', 'collection', 'extra', 'cancellation'].includes(rawLabel)) {
        return rawLabel;
    }

    if (rawLabel === 'proof') {
        const type = resolveFileType(file);
        return type === 'video' ? 'collection' : 'separation';
    }

    return 'extra';
}

function getFileBadgeMeta(label) {
    const normalized = resolveNormalizedFileLabel({ label });
    if (normalized === 'romaneio') {
        return {
            label: 'Romaneio',
            badgeClassName: 'bg-indigo-600 text-white',
            detailsClassName: 'text-indigo-700 dark:text-indigo-300',
            detailsText: 'Arquivo principal do romaneio da solicitacao.',
        };
    }
    if (normalized === 'separation') {
        return {
            label: 'Separacao',
            badgeClassName: 'bg-green-600 text-white',
            detailsClassName: 'text-green-700 dark:text-green-300',
            detailsText: 'Evidencia enviada na etapa de separacao.',
        };
    }
    if (normalized === 'collection') {
        return {
            label: 'Coleta',
            badgeClassName: 'bg-teal-600 text-white',
            detailsClassName: 'text-teal-700 dark:text-teal-300',
            detailsText: 'Evidencia enviada na confirmacao da coleta.',
        };
    }
    if (normalized === 'cancellation') {
        return {
            label: 'Cancelamento',
            badgeClassName: 'bg-red-600 text-white',
            detailsClassName: 'text-red-700 dark:text-red-300',
            detailsText: 'Arquivo enviado na acao de cancelamento.',
        };
    }
    return {
        label: 'Extra',
        badgeClassName: 'bg-emerald-600 text-white',
        detailsClassName: 'text-emerald-700 dark:text-emerald-300',
        detailsText: 'Adicionada fora do fluxo.',
    };
}

function resolveCancellationReasonFromNotes(notes = null) {
    if (!notes) {
        return null;
    }

    if (typeof notes === 'string') {
        const normalized = notes.trim();
        return normalized || null;
    }

    const reason = String(notes?.reason || '').trim();
    if (reason) {
        return reason;
    }

    const text = String(notes?.text || '').trim();
    return text || null;
}

const OutboundDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const { checkPermission, user } = useAuth();

    const [outbound, setOutbound] = useState(null);
    const [items, setItems] = useState([]);
    const [stages, setStages] = useState([]);
    const [uploadedPhotos, setUploadedPhotos] = useState([]);
    const [pendingPhotos, setPendingPhotos] = useState([]);
    const [pendingCollectionVideos, setPendingCollectionVideos] = useState([]);
    const [oversizedVideos, setOversizedVideos] = useState([]);
    const [showStageDetails, setShowStageDetails] = useState(false);
    const [viewer, setViewer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [collecting, setCollecting] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [extraFiles, setExtraFiles] = useState([]);
    const [uploadingExtraFiles, setUploadingExtraFiles] = useState(false);
    const [extraFilesModalOpen, setExtraFilesModalOpen] = useState(false);
    const [cancelModal, setCancelModal] = useState({
        isOpen: false,
        reason: '',
        confirmationText: '',
        files: [],
    });
    const [cancelling, setCancelling] = useState(false);
    const [viewerImageMeta, setViewerImageMeta] = useState(null);
    const [viewerPdfUrl, setViewerPdfUrl] = useState(null);
    const [viewerPdfError, setViewerPdfError] = useState('');
    const [timerNow, setTimerNow] = useState(() => Date.now());
    const [hauliers, setHauliers] = useState([]);
    const [loadingHauliers, setLoadingHauliers] = useState(false);
    const [updatingTransport, setUpdatingTransport] = useState(false);
    const [transportForm, setTransportForm] = useState({
        haulierOrganizationId: '',
        driverName: '',
        driverDocument: '',
        vehiclePlate: '',
    });

    const canUpdateStatus = checkPermission('wms:status:update');
    const canViewStageSla = checkPermission('wms:outbound:create');
    const canManageOutbound = checkPermission('wms:outbound:create');
    const isOutboundCreator = Boolean(user?.id && outbound?.user_id && String(user.id) === String(outbound.user_id));
    const canManageTransport = canManageOutbound || isOutboundCreator;
    const isTransportLocked = String(outbound?.status || '').trim() === 'collected';
    const canEditTransport = canManageTransport && !isTransportLocked;
    const hasTransportAssignment = Boolean(
        outbound?.haulier_organization_id &&
            outbound?.driver_name &&
            outbound?.driver_document &&
            outbound?.vehicle_plate,
    );

    const refreshOutbound = useCallback(async () => {
        const data = await api.getOutbound(id);
        setOutbound(data);
        setItems(Array.isArray(data?.items) ? data.items : []);
        const resolvedStages = Array.isArray(data?.stages)
            ? data.stages
            : Array.isArray(data?.timeline)
              ? data.timeline
              : [];
        setStages(resolvedStages);
        setUploadedPhotos(Array.isArray(data?.photos) ? data.photos : Array.isArray(data?.files) ? data.files : []);
        return data;
    }, [id]);

    useEffect(() => {
        refreshOutbound()
            .catch((error) => {
                toast.error(error.message || 'Erro ao carregar outbound');
            })
            .finally(() => {
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshOutbound]);

    useEffect(() => {
        if (!canEditTransport) {
            return;
        }

        let active = true;
        setLoadingHauliers(true);
        api.getWmsHauliers({ limit: 500 })
            .then((data) => {
                if (!active) {
                    return;
                }
                const rows = Array.isArray(data?.results) ? data.results : [];
                setHauliers(rows);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }
                toast.error(error.message || 'Erro ao carregar transportadoras');
            })
            .finally(() => {
                if (active) {
                    setLoadingHauliers(false);
                }
            });

        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canEditTransport]);

    useEffect(() => {
        setTransportForm({
            haulierOrganizationId: String(outbound?.haulier_organization_id || ''),
            driverName: String(outbound?.driver_name || ''),
            driverDocument: String(outbound?.driver_document || ''),
            vehiclePlate: String(outbound?.vehicle_plate || ''),
        });
    }, [outbound?.haulier_organization_id, outbound?.driver_name, outbound?.driver_document, outbound?.vehicle_plate]);

    useEffect(() => {
        setViewerImageMeta(null);
        if (!viewer || viewer.type !== 'image' || !viewer.url) {
            return;
        }

        let active = true;
        const image = new Image();
        image.onload = () => {
            if (!active) {
                return;
            }
            setViewerImageMeta({
                width: image.naturalWidth || null,
                height: image.naturalHeight || null,
            });
        };
        image.onerror = () => {
            if (!active) {
                return;
            }
            setViewerImageMeta(null);
        };
        image.src = viewer.url;

        return () => {
            active = false;
        };
    }, [viewer]);

    useEffect(() => {
        setViewerPdfUrl(null);
        setViewerPdfError('');
        if (!viewer || viewer.type !== 'pdf' || !viewer.url) {
            return;
        }

        let active = true;
        let objectUrl = null;

        fetch(viewer.url)
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                return response.blob();
            })
            .then((blob) => {
                if (!active) {
                    return;
                }
                objectUrl = URL.createObjectURL(blob);
                setViewerPdfUrl(objectUrl);
            })
            .catch(() => {
                if (!active) {
                    return;
                }
                setViewerPdfError('Nao foi possivel carregar o preview PDF no modal.');
            });

        return () => {
            active = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [viewer]);

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            setTimerNow(Date.now());
        }, 1000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, []);

    const updateTransportField = (field, value) => {
        setTransportForm((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const handleSaveTransport = async () => {
        if (!canManageTransport) {
            toast.error('Voce nao tem permissao para alterar os dados de transporte deste outbound.');
            return;
        }
        if (isTransportLocked) {
            toast.error('Nao e possivel alterar transporte apos o outbound ser coletado.');
            return;
        }

        const haulierOrganizationId = String(transportForm.haulierOrganizationId || '').trim();
        const driverName = String(transportForm.driverName || '').trim();
        const driverDocument = String(transportForm.driverDocument || '').trim();
        const vehiclePlate = String(transportForm.vehiclePlate || '')
            .trim()
            .toUpperCase()
            .replace(/\s+/g, '');

        if (!haulierOrganizationId || !driverName || !driverDocument || !vehiclePlate) {
            toast.error('Informe transportadora, motorista, documento e placa para salvar.');
            return;
        }

        try {
            setUpdatingTransport(true);
            await api.updateOutboundTransport(id, {
                haulier_organization_id: haulierOrganizationId,
                driver_name: driverName,
                driver_document: driverDocument,
                vehicle_plate: vehiclePlate,
            });
            await refreshOutbound();
            toast.success('Dados de transporte atualizados.');
        } catch (error) {
            toast.error(error.message || 'Erro ao salvar dados de transporte');
        } finally {
            setUpdatingTransport(false);
        }
    };

    const handleClearTransport = async () => {
        if (!canManageTransport) {
            toast.error('Voce nao tem permissao para alterar os dados de transporte deste outbound.');
            return;
        }
        if (isTransportLocked) {
            toast.error('Nao e possivel alterar transporte apos o outbound ser coletado.');
            return;
        }

        try {
            setUpdatingTransport(true);
            await api.updateOutboundTransport(id, {
                haulier_organization_id: null,
                driver_name: null,
                driver_document: null,
                vehicle_plate: null,
            });
            await refreshOutbound();
            toast.success('Vinculo de transporte removido.');
        } catch (error) {
            toast.error(error.message || 'Erro ao remover transporte');
        } finally {
            setUpdatingTransport(false);
        }
    };

    const handlePendingPhotosChange = (files) => {
        const all = Array.from(files || []).filter((file) => String(file?.type || '').startsWith('image/'));
        // Validacao de TAMANHO no front (antes so o backend barrava). Imagem <= 10MB.
        const tooBig = all.filter((f) => Number(f?.size || 0) > 10 * 1024 * 1024);
        if (tooBig.length) {
            toast.error(`${tooBig.length} imagem(ns) acima de 10MB foram ignoradas.`);
        }
        const incoming = all.filter((f) => Number(f?.size || 0) <= 10 * 1024 * 1024);
        setPendingPhotos(incoming.slice(0, MAX_PHOTOS));
    };

    const handlePendingCollectionVideosChange = (files) => {
        const all = Array.from(files || []).filter((file) => String(file?.type || '').startsWith('video/'));
        // Video de coleta <= MAX_VIDEO_MB (limite do backend). Os que passam NAO sao
        // descartados em silencio: ficam listados num aviso fixo abaixo do uploader
        // (o operador ve exatamente quais e o que fazer), e os validos seguem prontos.
        const tooBig = all.filter((f) => Number(f?.size || 0) > MAX_VIDEO_BYTES);
        setOversizedVideos(
            tooBig.map((f) => ({
                name: f?.name || 'video',
                sizeMb: Math.round(Number(f?.size || 0) / (1024 * 1024)),
            })),
        );
        if (tooBig.length) {
            toast.error(
                `${tooBig.length} video(s) acima de ${MAX_VIDEO_MB}MB nao serao enviados (veja o aviso abaixo).`,
            );
        }
        const incoming = all.filter((f) => Number(f?.size || 0) <= MAX_VIDEO_BYTES);
        setPendingCollectionVideos(incoming.slice(0, MAX_COLLECTION_VIDEOS));
    };

    const handleUploadPhotos = async () => {
        if (!canUpdateStatus) {
            toast.error('Voce nao tem permissao para atualizar status no WMS');
            return;
        }

        if (!pendingPhotos || pendingPhotos.length < MIN_PHOTOS_TO_COMPLETE) {
            toast.error(
                `Voce selecionou apenas ${pendingPhotos ? pendingPhotos.length : 0} fotos. O minimo e ${MIN_PHOTOS_TO_COMPLETE} para finalizar a separacao.`,
            );
            return;
        }

        setUploading(true);

        try {
            for (const photo of pendingPhotos) {
                const formData = new FormData();
                formData.append('photos', photo);
                await api.uploadOutboundPhotos(id, formData);
            }
            await api.updateOutboundStatus(id, 'ready_to_collect');
            await refreshOutbound();
            setPendingPhotos([]);
            toast.success('Fotos enviadas e status atualizado');
        } catch (error) {
            toast.error(error.message || 'Erro ao enviar fotos');
        } finally {
            setUploading(false);
        }
    };

    const handleConfirmCollection = async () => {
        if (!canUpdateStatus) {
            toast.error('Voce nao tem permissao para atualizar status no WMS');
            return;
        }

        if (!hasTransportAssignment) {
            toast.error('Antes de confirmar coleta, informe transportadora, motorista e placa.');
            return;
        }

        if (!pendingCollectionVideos || pendingCollectionVideos.length < 1) {
            toast.error('Para marcar como coletado e obrigatorio enviar pelo menos 1 video.');
            return;
        }

        try {
            setCollecting(true);
            for (const video of pendingCollectionVideos) {
                const formData = new FormData();
                formData.append('photos', video);
                await api.uploadOutboundPhotos(id, formData);
            }

            await api.updateOutboundStatus(id, 'collected');
            await refreshOutbound();
            setPendingCollectionVideos([]);
            setOversizedVideos([]);
            toast.success('Video enviado e coleta confirmada.');
        } catch (error) {
            toast.error(error.message || 'Erro ao confirmar coleta');
        } finally {
            setCollecting(false);
        }
    };

    const resolvePrimaryAction = (status) => {
        if (!canUpdateStatus) {
            return null;
        }

        if (status === 'idle') {
            return {
                label: 'Iniciar separacao',
                nextStatus: 'picking',
            };
        }

        return null;
    };

    const handleAdvanceStage = async () => {
        const action = resolvePrimaryAction(outbound?.status);
        if (!action) {
            return;
        }

        try {
            setUpdatingStatus(true);
            await api.updateOutboundStatus(id, action.nextStatus);
            await refreshOutbound();
            toast.success('Status atualizado com sucesso.');
        } catch (error) {
            toast.error(error.message || 'Erro ao atualizar status');
        } finally {
            setUpdatingStatus(false);
        }
    };

    const uploadExtraFiles = async () => {
        if (!canManageOutbound) {
            toast.error('Voce nao tem permissao para anexar arquivos extras.');
            return;
        }
        if (extraFiles.length === 0) {
            toast.error('Selecione ao menos um arquivo extra.');
            return;
        }

        try {
            setUploadingExtraFiles(true);
            for (const file of extraFiles) {
                const formData = new FormData();
                formData.append('label', 'extra');
                formData.append('files', file);
                await api.uploadOutboundFiles(id, formData);
            }
            await refreshOutbound();
            setExtraFiles([]);
            setExtraFilesModalOpen(false);
            toast.success('Arquivos extras enviados.');
        } catch (error) {
            toast.error(error.message || 'Erro ao enviar arquivos extras');
        } finally {
            setUploadingExtraFiles(false);
        }
    };

    const openCancelModal = () => {
        setCancelModal({
            isOpen: true,
            reason: '',
            confirmationText: '',
            files: [],
        });
    };

    const closeCancelModal = () => {
        if (cancelling) {
            return;
        }
        setCancelModal({
            isOpen: false,
            reason: '',
            confirmationText: '',
            files: [],
        });
    };

    const updateCancelField = (field, value) => {
        setCancelModal((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const executeCancellation = async () => {
        const reason = String(cancelModal.reason || '').trim();
        const confirmationText = String(cancelModal.confirmationText || '').trim();
        if (!reason) {
            toast.error('Informe o motivo do cancelamento.');
            return;
        }

        if (confirmationText !== 'Cancelar') {
            toast.error('Digite exatamente "Cancelar" para confirmar.');
            return;
        }

        try {
            setCancelling(true);
            const formData = new FormData();
            formData.append('reason', reason);
            formData.append('confirmation_text', confirmationText);
            await api.cancelOutbound(id, formData);

            for (const file of cancelModal.files || []) {
                const uploadFormData = new FormData();
                uploadFormData.append('label', 'cancellation');
                uploadFormData.append('files', file);
                await api.uploadOutboundFiles(id, uploadFormData);
            }

            await refreshOutbound();
            closeCancelModal();
            toast.success('Outbound cancelado com sucesso.');
        } catch (error) {
            toast.error(error.message || 'Erro ao cancelar outbound');
        } finally {
            setCancelling(false);
        }
    };

    const normalizedUploadedPhotos = useMemo(() => {
        return (uploadedPhotos || [])
            .map((photo, index) => {
                const rawUrl = photo?.photo_url || photo?.url || photo;
                const url = ResolveAssetUrl(rawUrl);
                if (!url) {
                    return null;
                }

                return {
                    id: photo?.id || `photo-${index}`,
                    url,
                    label: String(photo?.label || 'proof'),
                    mime_type: photo?.mime_type || null,
                    original_name: photo?.original_name || null,
                    file_size_bytes: photo?.file_size_bytes || null,
                    uploaded_by_user_id: photo?.uploaded_by_user_id || null,
                    uploaded_by_user_name: photo?.uploaded_by_user_name || null,
                    createdAt: photo?.created_at || null,
                };
            })
            .filter(Boolean);
    }, [uploadedPhotos]);

    const fileGalleryItems = useMemo(() => {
        const files = [];
        if (outbound?.original_pdf_url) {
            const url = ResolveAssetUrl(outbound.original_pdf_url);
            if (url) {
                files.push({
                    id: 'outbound-pdf',
                    type: 'pdf',
                    url,
                    title: 'Romaneio',
                    label: 'romaneio',
                    mime_type: 'application/pdf',
                    original_name: 'Romaneio.pdf',
                    file_size_bytes: outbound?.original_pdf_file_size_bytes || null,
                    uploaded_by_user_name: outbound?.user_name || null,
                    createdAt: outbound?.created_at || null,
                });
            }
        }

        for (const photo of normalizedUploadedPhotos) {
            const type = resolveFileType(photo);
            const normalizedLabel = resolveNormalizedFileLabel({
                label: photo.label,
                mime_type: photo.mime_type,
                url: photo.url,
            });
            files.push({
                id: `photo-${photo.id}`,
                type,
                url: photo.url,
                title:
                    normalizedLabel === 'cancellation'
                        ? 'Arquivo de cancelamento'
                        : normalizedLabel === 'extra'
                          ? 'Arquivo extra'
                          : normalizedLabel === 'collection'
                            ? 'Video da coleta'
                            : normalizedLabel === 'separation'
                              ? 'Arquivo de separacao'
                              : type === 'image'
                                ? 'Imagem da carga'
                                : 'Arquivo da carga',
                label: normalizedLabel,
                mime_type: photo.mime_type,
                original_name: photo.original_name,
                file_size_bytes: photo.file_size_bytes,
                uploaded_by_user_name: photo.uploaded_by_user_name || null,
                createdAt: photo.createdAt || null,
            });
        }

        return files.sort((a, b) => {
            const timeA = toDate(a.createdAt)?.getTime() || 0;
            const timeB = toDate(b.createdAt)?.getTime() || 0;
            return timeB - timeA;
        });
    }, [
        outbound?.original_pdf_url,
        outbound?.original_pdf_file_size_bytes,
        outbound?.created_at,
        outbound?.user_name,
        normalizedUploadedPhotos,
    ]);

    const viewerBadgeMeta = useMemo(() => {
        if (!viewer) {
            return null;
        }
        return getFileBadgeMeta(viewer.label);
    }, [viewer]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20 text-mono-500">
                <div className="mr-3 h-10 w-10 animate-spin rounded-full border-b-2 border-black dark:border-white" />
                Carregando detalhes...
            </div>
        );
    }

    if (!outbound) {
        return (
            <div className="p-6 text-center">
                <Warning size={48} className="mx-auto mb-4 text-mono-400" />
                <h2 className="text-xl font-bold dark:text-white">Outbound nao encontrado.</h2>
                <Button onClick={() => navigate('/wms')} variant="ghost" className="mt-4">
                    Voltar ao dashboard
                </Button>
            </div>
        );
    }

    const isCancelled = outbound.status === 'rejected';
    const cancellationReasonFromStages = [...(stages || [])]
        .sort((a, b) => Number(b?.sequence || 0) - Number(a?.sequence || 0))
        .map((entry) => resolveCancellationReasonFromNotes(entry?.notes))
        .find((value) => Boolean(value));
    const resolvedCancellationReason = outbound?.cancellation?.reason || cancellationReasonFromStages || null;
    const config = isCancelled
        ? {
              ...WMS_STATUS_CONFIG.rejected,
              label: 'Cancelado',
          }
        : WMS_STATUS_CONFIG[outbound.status] || WMS_STATUS_CONFIG.pending;
    const Icon = config.icon;
    const isTerminalStatus = TERMINAL_STATUSES.has(outbound.status);
    const stageByStatus = new Map();

    stages.forEach((entry) => {
        const status = String(entry?.status || entry?.to_status || '').trim();
        if (status) {
            stageByStatus.set(status, entry);
        }
    });

    const completedStages = stages
        .filter((entry) => toDate(entry?.completed_at || entry?.ended_at))
        .sort((a, b) => Number(a?.sequence || 0) - Number(b?.sequence || 0));
    const visitedStatusFlow = [
        ...new Set(
            completedStages.map((entry) => String(entry?.status || entry?.to_status || '').trim()).filter(Boolean),
        ),
    ];
    if (outbound?.status && !visitedStatusFlow.includes(outbound.status)) {
        visitedStatusFlow.push(outbound.status);
    }
    const stageFlow =
        outbound.status === 'rejected'
            ? visitedStatusFlow.length > 0
                ? visitedStatusFlow
                : flowRejected
            : flowDefault;
    const displayCurrentStatus = outbound.status === 'ready_to_collect' ? 'collected' : outbound.status;
    const currentFlowIndex = stageFlow.indexOf(displayCurrentStatus);
    const primaryAction = resolvePrimaryAction(outbound.status);
    const readyToCollectEntry = stageByStatus.get('ready_to_collect') || null;
    const defaultCurrentStageEntry = stageByStatus.get(displayCurrentStatus) || null;
    const currentStageStartDate =
        outbound.status === 'ready_to_collect'
            ? toDate(readyToCollectEntry?.completed_at || readyToCollectEntry?.ended_at)
            : toDate(defaultCurrentStageEntry?.started_at);

    const stageDetailsRows = (() => {
        const sourceRows = Array.isArray(stages) ? [...stages] : [];
        if (outbound.status === 'ready_to_collect') {
            const hasCollectedRow = sourceRows.some(
                (entry) => String(entry?.status || entry?.to_status || '').trim() === 'collected',
            );
            if (!hasCollectedRow && currentStageStartDate) {
                const maxSequence = sourceRows.reduce((acc, entry) => {
                    const sequence = Number(entry?.sequence || 0);
                    return sequence > acc ? sequence : acc;
                }, 0);

                sourceRows.push({
                    id: `synthetic-collected-${outbound.id}`,
                    status: 'collected',
                    to_status: 'collected',
                    started_at: currentStageStartDate.toISOString(),
                    completed_at: null,
                    ended_at: null,
                    next_status: null,
                    processed_by_user_id: null,
                    processed_by_user_name: null,
                    notes: null,
                    sequence: maxSequence + 1,
                });
            }
        }

        return sourceRows
            .filter((entry) => {
                const status = String(entry?.status || entry?.to_status || '').trim();
                return Boolean(status && stageFlow.includes(status));
            })
            .sort((a, b) => Number(a?.sequence || 0) - Number(b?.sequence || 0));
    })();

    const resolveStageDurationLabel = (row) => {
        const completedAt = toDate(row?.completed_at || row?.ended_at);
        if (completedAt) {
            const startedAt = toDate(row?.started_at);
            const durationSeconds =
                startedAt && completedAt
                    ? Math.max(0, Math.floor((completedAt.getTime() - startedAt.getTime()) / 1000))
                    : null;
            return formatDuration(durationSeconds ?? row?.duration_seconds);
        }

        const startedAt = toDate(row?.started_at);
        if (!startedAt) {
            return '-';
        }

        const elapsedSeconds = Math.max(0, Math.floor((timerNow - startedAt.getTime()) / 1000));
        return formatDuration(elapsedSeconds);
    };

    const stageDetailsColumns = [
        {
            key: 'status',
            label: 'Etapa',
            sortable: false,
            getValue: (row) => {
                const status = String(row?.status || row?.to_status || '').trim();
                return WMS_STATUS_CONFIG[status]?.label || status || '-';
            },
            cellClassName: 'text-black dark:text-white',
        },
        {
            key: 'started_at',
            label: 'Inicio',
            sortable: false,
            getValue: (row) => {
                const startedAt = toDate(row?.started_at);
                return FormatDateTimeBr(startedAt, { blank: '-' });
            },
        },
        {
            key: 'completed_at',
            label: 'Fim',
            sortable: false,
            getValue: (row) => {
                const completedAt = toDate(row?.completed_at || row?.ended_at);
                return FormatDateTimeBr(completedAt, { blank: '-' });
            },
        },
        {
            key: 'next_status',
            label: 'Proxima',
            sortable: false,
            getValue: (row) => {
                const nextStatus = String(row?.next_status || '').trim();
                return nextStatus ? WMS_STATUS_CONFIG[nextStatus]?.label || nextStatus : '-';
            },
        },
        {
            key: 'processed_by_user_name',
            label: 'Usuario',
            sortable: false,
            getValue: (row) =>
                row?.processed_by_user_name || row?.processed_by_user_id || row?.changed_by_user_id || '-',
        },
        {
            key: 'cancellation_reason',
            label: 'Motivo cancelamento',
            sortable: false,
            getValue: (row) => {
                const reason = resolveCancellationReasonFromNotes(row?.notes || null);
                return reason || '-';
            },
        },
        {
            key: 'duration_seconds',
            label: 'SLA',
            align: 'right',
            sortable: false,
            searchable: false,
            getValue: (row) => resolveStageDurationLabel(row),
            cellClassName: 'text-black dark:text-white',
        },
    ];

    const itemColumns = [
        {
            key: 'sku',
            label: 'SKU',
            getValue: (row) => row.sku || '-',
            cellClassName: 'font-mono font-bold text-black dark:text-white',
        },
        {
            key: 'description',
            label: 'Descricao',
            getValue: (row) => row.description || '-',
            cellClassName: 'text-mono-700 dark:text-mono-300',
        },
        {
            key: 'ean',
            label: 'EAN',
            getValue: (row) => row.ean || '-',
            cellClassName: 'font-mono text-black dark:text-white',
        },
        {
            key: 'units',
            label: 'Unidades',
            align: 'center',
            searchable: false,
            getValue: (row) => Number(row.units || 0),
        },
        {
            key: 'pallets_needed',
            label: 'Pallets necessarios',
            align: 'right',
            searchable: false,
            getValue: (row) => Number.parseFloat(row.pallets_needed || 0).toFixed(2),
            cellClassName: 'font-semibold text-black dark:text-white',
        },
    ];

    return (
        <div className="space-y-6">
            <PageHeader
                title="Detalhes do outbound"
                subtitle="Acompanhamento de status, itens e evidencias de separacao"
                actions={
                    <div className="flex flex-wrap items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/wms')}>
                            <ArrowLeft size={18} className="mr-2" />
                            Voltar
                        </Button>
                        {primaryAction ? (
                            <Button size="sm" onClick={handleAdvanceStage} loading={updatingStatus}>
                                {primaryAction.label}
                            </Button>
                        ) : null}
                        {canManageOutbound && !isTerminalStatus ? (
                            <Button size="sm" variant="danger" onClick={openCancelModal}>
                                Cancelar
                            </Button>
                        ) : null}
                        {isTerminalStatus ? (
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                    const exportPayload = {
                                        outbound,
                                        items,
                                        stages,
                                        files: fileGalleryItems,
                                        exported_at: new Date().toISOString(),
                                    };
                                    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], {
                                        type: 'application/json',
                                    });
                                    const url = URL.createObjectURL(blob);
                                    const anchor = document.createElement('a');
                                    anchor.href = url;
                                    anchor.download = `outbound-${outbound.id}.json`;
                                    document.body.appendChild(anchor);
                                    anchor.click();
                                    document.body.removeChild(anchor);
                                    URL.revokeObjectURL(url);
                                }}
                            >
                                Exportar
                            </Button>
                        ) : null}
                    </div>
                }
            />
            <Card title="Stages">
                <div className="space-y-6">
                    <div className="overflow-x-auto">
                        <ol className="flex w-full items-start font-medium text-xs sm:text-sm text-mono-900 dark:text-mono-100">
                            {stageFlow.map((status, index) => {
                                const stage = stageByStatus.get(status) || null;
                                const stageEndedAt = toDate(stage?.completed_at || stage?.ended_at);
                                const isCurrent = !isTerminalStatus && index === currentFlowIndex;
                                const isCompleted =
                                    Boolean(stageEndedAt) || (currentFlowIndex >= 0 && index < currentFlowIndex);
                                const isCancelledStage = status === 'rejected';
                                const isAwaitingCollectionConfirmation =
                                    outbound.status === 'ready_to_collect' && status === 'collected' && isCurrent;
                                const label = isCancelledStage
                                    ? 'Cancelado'
                                    : WMS_STATUS_CONFIG[status]?.label || status;
                                const stageDurationLabel = resolveStageDurationLabel(stage);
                                const canShowStageDuration =
                                    canViewStageSla && (status === 'idle' || status === 'picking');
                                const subLabel = isCancelledStage
                                    ? 'Processo cancelado'
                                    : isAwaitingCollectionConfirmation
                                      ? 'Aguardando confirmacao'
                                      : isCurrent
                                        ? 'Etapa atual'
                                        : isCompleted
                                          ? canShowStageDuration
                                              ? stageDurationLabel === '-'
                                                  ? 'Concluida'
                                                  : stageDurationLabel
                                              : 'Concluida'
                                          : '-';

                                return (
                                    <li
                                        key={status}
                                        className={`relative flex min-w-0 flex-1 basis-0 ${
                                            index > 0
                                                ? `before:absolute before:left-0 before:right-1/2 before:top-3 lg:before:top-5 before:h-0.5 ${
                                                      index <= currentFlowIndex
                                                          ? 'before:bg-green-600 dark:before:bg-green-500'
                                                          : 'before:bg-mono-200 dark:before:bg-mono-800'
                                                  }`
                                                : ''
                                        } ${
                                            index < stageFlow.length - 1
                                                ? `after:absolute after:left-1/2 after:right-0 after:top-3 lg:after:top-5 after:h-0.5 ${
                                                      index < currentFlowIndex
                                                          ? 'after:bg-green-600 dark:after:bg-green-500'
                                                          : 'after:bg-mono-200 dark:after:bg-mono-800'
                                                  }`
                                                : ''
                                        }`}
                                    >
                                        <div className="z-10 block w-full whitespace-nowrap text-center">
                                            <span
                                                className={`mx-auto mb-3 flex h-6 w-6 items-center justify-center rounded-full border-2 text-sm lg:h-10 lg:w-10 ${
                                                    isCancelledStage
                                                        ? 'bg-red-600 text-white border-red-600'
                                                        : isCurrent
                                                          ? 'bg-white text-green-700 border-green-600 dark:bg-mono-950 dark:text-green-300 dark:border-green-500'
                                                          : isCompleted
                                                            ? 'bg-green-600 text-white border-green-600'
                                                            : 'bg-mono-50 text-mono-500 border-mono-300 dark:bg-mono-900 dark:text-mono-400 dark:border-mono-700'
                                                }`}
                                            >
                                                {isCancelledStage ? <X size={14} /> : index + 1}
                                            </span>
                                            <div>{label}</div>
                                            <div className="mt-1 text-[11px] text-mono-500 dark:text-mono-400">
                                                {subLabel}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    </div>

                    {canViewStageSla ? (
                        <div className="space-y-3">
                            <button
                                type="button"
                                className="text-sm font-medium text-green-700 hover:text-green-800 dark:text-green-300 dark:hover:text-green-200"
                                onClick={() => setShowStageDetails((value) => !value)}
                            >
                                {showStageDetails ? 'Ocultar detalhes' : 'Expandir detalhes'}
                            </button>

                            {showStageDetails ? (
                                stageDetailsRows.length > 0 ? (
                                    <StandardTable
                                        rows={stageDetailsRows}
                                        columns={stageDetailsColumns}
                                        variant="minimal"
                                        rowKey={(row, index) =>
                                            row?.id || `${String(row?.status || row?.to_status || 'stage')}-${index}`
                                        }
                                        emptyMessage="Sem etapas para exibir."
                                    />
                                ) : (
                                    <p className="text-sm text-mono-500 dark:text-mono-400">Sem etapas para exibir.</p>
                                )
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </Card>

            <Card>
                <div className="flex flex-col items-start justify-between gap-6 md:flex-row">
                    <div className="space-y-3">
                        <div>
                            <span className="text-xs font-bold uppercase tracking-wider text-mono-400">Outbound</span>
                            <h2 className="text-2xl font-bold text-black dark:text-white">
                                {outbound?.freight_code ? `Frete #${outbound.freight_code}` : outbound.id}
                            </h2>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                            <span
                                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${config.color}`}
                            >
                                <Icon size={14} weight="fill" />
                                {config.label}
                            </span>
                            {isCancelled ? (
                                <span className="text-xs font-medium text-red-700 dark:text-red-300">
                                    Motivo: {resolvedCancellationReason || 'Nao informado'}
                                </span>
                            ) : null}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-8 md:text-right">
                        <div>
                            <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-mono-400">
                                Total pallets
                            </span>
                            <div className="text-3xl font-bold text-black dark:text-white">
                                {Number.parseFloat(outbound.total_pallets).toFixed(2)}
                            </div>
                        </div>
                        <div />
                    </div>
                </div>

                <div className="mt-6 border-t border-mono-200 pt-6 dark:border-mono-800">
                    <div className="mb-3">
                        <h3 className="text-lg font-semibold text-black dark:text-white">Transporte da coleta</h3>
                        <p className="text-xs text-mono-500 dark:text-mono-400">
                            A coleta so pode ser confirmada com transportadora, motorista e placa informados.
                        </p>
                    </div>

                    {canEditTransport ? (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                <Select
                                    label="Transportadora"
                                    value={transportForm.haulierOrganizationId}
                                    onChange={(event) =>
                                        updateTransportField('haulierOrganizationId', event.target.value)
                                    }
                                    disabled={updatingTransport || loadingHauliers}
                                >
                                    <option value="">{loadingHauliers ? 'Carregando...' : 'Selecione'}</option>
                                    {hauliers.map((haulier) => (
                                        <option key={haulier.id} value={haulier.id}>
                                            {haulier.name}
                                        </option>
                                    ))}
                                </Select>
                                <Input
                                    label="Motorista"
                                    value={transportForm.driverName}
                                    onChange={(event) => updateTransportField('driverName', event.target.value)}
                                    placeholder="Nome do motorista"
                                    disabled={updatingTransport}
                                />
                                <Input
                                    label="Documento"
                                    value={transportForm.driverDocument}
                                    onChange={(event) => updateTransportField('driverDocument', event.target.value)}
                                    placeholder="CPF/CNPJ/RG"
                                    disabled={updatingTransport}
                                />
                                <Input
                                    label="Placa"
                                    value={transportForm.vehiclePlate}
                                    onChange={(event) => updateTransportField('vehiclePlate', event.target.value)}
                                    placeholder="ABC1234"
                                    disabled={updatingTransport}
                                />
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                                <Button onClick={handleSaveTransport} loading={updatingTransport}>
                                    Salvar transporte
                                </Button>
                                <Button variant="secondary" onClick={handleClearTransport} disabled={updatingTransport}>
                                    Remover vinculacao
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                                <div>
                                    <span className="text-xs text-mono-500 dark:text-mono-400">Transportadora</span>
                                    <p className="font-medium text-black dark:text-white">
                                        {outbound.haulier_name || '-'}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-xs text-mono-500 dark:text-mono-400">Motorista</span>
                                    <p className="font-medium text-black dark:text-white">
                                        {outbound.driver_name || '-'}
                                        {outbound.driver_document ? ` (${outbound.driver_document})` : ''}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-xs text-mono-500 dark:text-mono-400">Placa</span>
                                    <p className="font-medium text-black dark:text-white">
                                        {outbound.vehicle_plate || '-'}
                                    </p>
                                </div>
                            </div>
                            {isTransportLocked ? (
                                <p className="text-xs font-medium text-mono-600 dark:text-mono-300">
                                    Transporte bloqueado para edicao apos coleta.
                                </p>
                            ) : null}
                        </div>
                    )}

                    {!hasTransportAssignment ? (
                        <p className="mt-3 text-xs font-medium text-amber-700 dark:text-amber-300">
                            Transporte ainda nao atribuido.
                        </p>
                    ) : null}
                </div>
            </Card>
            {outbound.status === 'picking' ? (
                <Card>
                    <div className="mb-6 flex items-start gap-4">
                        <div className="rounded-xl bg-black p-3 text-white dark:bg-white dark:text-black">
                            <Camera size={24} weight="bold" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-black dark:text-white">Finalizar separacao</h3>
                            <p className="text-sm text-mono-600 dark:text-mono-400">
                                Para concluir, envie no minimo <strong>{MIN_PHOTOS_TO_COMPLETE} fotos</strong> da carga
                                pronta para coleta.
                            </p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <FileUploader
                            files={pendingPhotos}
                            onFilesChange={handlePendingPhotosChange}
                            multiple
                            maxFiles={MAX_PHOTOS}
                            accept="image/*"
                            showCameraOption
                            subtitle={`Fotos selecionadas: ${pendingPhotos.length}/${MAX_PHOTOS}`}
                            disabled={!canUpdateStatus}
                        />

                        <Button
                            onClick={handleUploadPhotos}
                            size="lg"
                            loading={uploading}
                            disabled={pendingPhotos.length < MIN_PHOTOS_TO_COMPLETE || !canUpdateStatus}
                        >
                            Confirmar
                        </Button>
                    </div>
                </Card>
            ) : null}
            {outbound.status === 'ready_to_collect' ? (
                <Card>
                    <div className="mb-6 flex items-start gap-4">
                        <div className="rounded-xl bg-black p-3 text-white dark:bg-white dark:text-black">
                            <Camera size={24} weight="bold" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-black dark:text-white">Confirmar coleta</h3>
                            <p className="text-sm text-mono-600 dark:text-mono-400">
                                Para marcar como coletado, envie no minimo <strong>1 video</strong> da carga pronta para
                                coleta.
                            </p>
                            {!hasTransportAssignment ? (
                                <p className="mt-2 text-xs font-medium text-amber-700 dark:text-amber-300">
                                    Preencha os dados de transporte acima antes de confirmar a coleta.
                                </p>
                            ) : null}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <FileUploader
                            files={pendingCollectionVideos}
                            onFilesChange={handlePendingCollectionVideosChange}
                            multiple
                            maxFiles={MAX_COLLECTION_VIDEOS}
                            accept="video/*"
                            subtitle={`Videos selecionados: ${pendingCollectionVideos.length}/${MAX_COLLECTION_VIDEOS} · ate ${MAX_VIDEO_MB}MB cada`}
                            disabled={!canUpdateStatus}
                        />

                        {oversizedVideos.length > 0 ? (
                            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm dark:border-red-800 dark:bg-red-950">
                                <p className="font-semibold text-red-800 dark:text-red-200">
                                    {oversizedVideos.length} video(s) acima de {MAX_VIDEO_MB}MB NAO serao enviados:
                                </p>
                                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-red-700 dark:text-red-300">
                                    {oversizedVideos.map((v) => (
                                        <li key={`${v.name}-${v.sizeMb}`}>
                                            {v.name} ({v.sizeMb}MB)
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-2 text-red-700 dark:text-red-300">
                                    Comprima ou regrave em menor resolucao/duracao e selecione novamente. Os videos
                                    abaixo do limite continuam prontos para envio.
                                </p>
                            </div>
                        ) : null}

                        <Button
                            onClick={handleConfirmCollection}
                            size="lg"
                            loading={collecting}
                            disabled={pendingCollectionVideos.length < 1 || !canUpdateStatus || !hasTransportAssignment}
                        >
                            Confirmar
                        </Button>
                    </div>
                </Card>
            ) : null}
            <StandardTable
                rows={items}
                columns={itemColumns}
                rowKey={(row, index) => row.id || `${row.sku}-${index}`}
                variant="minimal"
                emptyMessage="Nenhum item registrado nesta carga."
            />

            <Card>
                <div className="space-y-4">
                    <div className="flex items-center justify-between gap-3">
                        <h3 className="text-lg font-semibold text-black dark:text-white">Arquivos da carga</h3>
                        {canManageOutbound ? (
                            <Button variant="secondary" onClick={() => setExtraFilesModalOpen(true)}>
                                Anexar arquivo
                            </Button>
                        ) : null}
                    </div>

                    {fileGalleryItems.length > 0 ? (
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                            {fileGalleryItems.map((file) => {
                                const badge = getFileBadgeMeta(file.label);
                                return (
                                    <button
                                        key={file.id}
                                        type="button"
                                        onClick={() =>
                                            setViewer({
                                                ...file,
                                                title: file.title || 'Arquivo',
                                            })
                                        }
                                        className="group relative aspect-square overflow-hidden rounded-xl border border-mono-200 bg-mono-50 text-left dark:border-mono-800 dark:bg-mono-900"
                                    >
                                        {file.type === 'pdf' ? (
                                            <div className="relative h-full w-full overflow-hidden bg-white dark:bg-mono-100">
                                                <div className="absolute inset-0 overflow-hidden">
                                                    <iframe
                                                        title="Miniatura A4 do PDF"
                                                        src={`${file.url}#page=1&view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
                                                        scrolling="no"
                                                        className="pointer-events-none absolute left-0 top-0 h-full w-full origin-top scale-[1.55]"
                                                    />
                                                </div>
                                            </div>
                                        ) : file.type === 'image' ? (
                                            <img
                                                src={file.url}
                                                alt="Imagem da carga"
                                                className="h-full w-full object-cover transition-transform group-hover:scale-105"
                                            />
                                        ) : (
                                            <div className="flex h-full flex-col items-center justify-center px-3 text-center">
                                                <span className="mb-1 rounded-full bg-mono-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-mono-700 dark:bg-mono-800 dark:text-mono-300">
                                                    Sem preview
                                                </span>
                                                <span className="text-xs text-mono-500 dark:text-mono-400">
                                                    Toque para abrir ou baixar
                                                </span>
                                            </div>
                                        )}
                                        <div className="pointer-events-none absolute left-2 top-2 z-10">
                                            <span
                                                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${badge.badgeClassName}`}
                                            >
                                                {badge.label}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-sm text-mono-500 dark:text-mono-400">
                            Nenhum arquivo enviado para este outbound.
                        </p>
                    )}
                </div>
            </Card>

            {viewer ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                    <button
                        type="button"
                        aria-label="Fechar visualizador"
                        className="absolute inset-0"
                        onClick={() => setViewer(null)}
                    />
                    <div className="relative z-10 flex h-[90vh] w-[96vw] max-w-[1700px] flex-col overflow-hidden rounded-xl border border-mono-200 bg-white shadow-xl sm:h-[88vh] sm:w-[94vw] lg:h-[85vh] lg:w-[85vw] dark:border-mono-800 dark:bg-black">
                        <div className="flex items-center justify-between border-b border-mono-200 px-4 py-3 dark:border-mono-800">
                            <h4 className="text-sm font-semibold text-black dark:text-white">
                                {viewer.title || 'Visualizador'}
                            </h4>
                            <button
                                type="button"
                                className="rounded p-1 text-mono-500 hover:bg-mono-100 hover:text-mono-700 dark:hover:bg-mono-900 dark:hover:text-mono-300"
                                onClick={() => setViewer(null)}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="relative min-h-0 flex-1 overflow-hidden bg-mono-50 dark:bg-mono-950">
                            {viewer.type === 'pdf' ? (
                                <div className="h-full w-full">
                                    <iframe
                                        title="Visualizador de PDF"
                                        src={viewerPdfUrl || viewer.url}
                                        className="h-full w-full overflow-hidden"
                                    />
                                    {viewerPdfError ? (
                                        <div className="absolute inset-x-3 bottom-3 rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-700">
                                            {viewerPdfError}
                                        </div>
                                    ) : null}
                                </div>
                            ) : viewer.type === 'image' ? (
                                <div className="flex h-full w-full items-center justify-center overflow-hidden p-2 sm:p-3">
                                    <img
                                        src={viewer.url}
                                        alt="Arquivo enviado"
                                        className="h-auto max-h-full w-auto max-w-full object-contain"
                                    />
                                </div>
                            ) : viewer.type === 'video' ? (
                                <div className="flex h-full w-full items-center justify-center overflow-hidden p-2 sm:p-3">
                                    <video
                                        src={viewer.url}
                                        controls
                                        className="h-auto max-h-full w-auto max-w-full rounded-lg bg-black object-contain"
                                    />
                                </div>
                            ) : (
                                <div className="flex h-full flex-col items-center justify-center gap-3 px-4 text-center">
                                    <p className="text-sm text-mono-600 dark:text-mono-300">
                                        Este tipo de arquivo nao possui preview direto.
                                    </p>
                                    <div className="flex flex-wrap items-center justify-center gap-2">
                                        <a
                                            href={viewer.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="rounded-lg border border-mono-300 px-3 py-1.5 text-sm font-medium text-mono-700 hover:bg-mono-100 dark:border-mono-700 dark:text-mono-200 dark:hover:bg-mono-900"
                                        >
                                            Abrir arquivo
                                        </a>
                                        <a
                                            href={viewer.url}
                                            download
                                            className="rounded-lg border border-mono-300 px-3 py-1.5 text-sm font-medium text-mono-700 hover:bg-mono-100 dark:border-mono-700 dark:text-mono-200 dark:hover:bg-mono-900"
                                        >
                                            Baixar
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="grid gap-2 border-t border-mono-200 px-4 py-3 text-xs text-mono-600 dark:border-mono-800 dark:text-mono-400 md:grid-cols-2">
                            {viewerBadgeMeta ? (
                                <div className={`md:col-span-2 font-medium ${viewerBadgeMeta.detailsClassName}`}>
                                    {viewerBadgeMeta.label}: {viewerBadgeMeta.detailsText}
                                </div>
                            ) : null}
                            <div>Etiqueta: {viewerBadgeMeta?.label || '-'}</div>
                            <div>Recebido em: {FormatDateTimeBr(viewer.createdAt, { blank: '-' })}</div>
                            <div>Enviado por: {viewer.uploaded_by_user_name || '-'}</div>
                            <div>Tamanho: {FormatFileSize(viewer.file_size_bytes)}</div>
                            <div>Formato: {resolveFileFormat(viewer)}</div>
                            {viewer.type === 'image' ? (
                                <div className="md:col-span-2">
                                    Resolucao:{' '}
                                    {viewerImageMeta?.width && viewerImageMeta?.height
                                        ? `${viewerImageMeta.width} x ${viewerImageMeta.height}`
                                        : '-'}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : null}

            <Modal
                isOpen={extraFilesModalOpen}
                onClose={() => {
                    if (uploadingExtraFiles) {
                        return;
                    }
                    setExtraFilesModalOpen(false);
                }}
                title="Anexar arquivo"
                size="lg"
                actions={
                    <>
                        <Button
                            variant="secondary"
                            onClick={() => setExtraFilesModalOpen(false)}
                            disabled={uploadingExtraFiles}
                        >
                            Voltar
                        </Button>
                        <Button
                            onClick={uploadExtraFiles}
                            loading={uploadingExtraFiles}
                            disabled={extraFiles.length === 0}
                        >
                            Enviar arquivos
                        </Button>
                    </>
                }
            >
                <FileUploader
                    files={extraFiles}
                    onFilesChange={(files) =>
                        setExtraFiles(Array.isArray(files) ? files.slice(0, MAX_EXTRA_FILES) : [])
                    }
                    multiple
                    maxFiles={MAX_EXTRA_FILES}
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.eml,.msg"
                    showCameraOption
                    subtitle={`Arquivos selecionados: ${extraFiles.length}/${MAX_EXTRA_FILES}`}
                />
            </Modal>

            <Modal
                isOpen={cancelModal.isOpen}
                onClose={closeCancelModal}
                title="Cancelar outbound"
                size="lg"
                actions={
                    <>
                        <Button variant="secondary" onClick={closeCancelModal} disabled={cancelling}>
                            Voltar
                        </Button>
                        <Button variant="danger" onClick={executeCancellation} loading={cancelling}>
                            Confirmar cancelamento
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <p className="text-sm text-mono-600 dark:text-mono-400">
                        Para cancelar, informe o motivo e digite <strong>Cancelar</strong>.
                    </p>
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                            Motivo
                        </label>
                        <Textarea
                            rows={4}
                            value={cancelModal.reason}
                            onChange={(event) => updateCancelField('reason', event.target.value)}
                            placeholder="Descreva o motivo do cancelamento"
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                            Confirmacao
                        </label>
                        <Input
                            value={cancelModal.confirmationText}
                            onChange={(event) => updateCancelField('confirmationText', event.target.value)}
                            placeholder='Digite "Cancelar"'
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                            Arquivos (opcional)
                        </label>
                        <FileUploader
                            files={cancelModal.files || []}
                            onFilesChange={(files) => updateCancelField('files', Array.isArray(files) ? files : [])}
                            multiple
                            maxFiles={MAX_EXTRA_FILES}
                            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.eml,.msg"
                            showCameraOption
                            title="Anexar evidencias de cancelamento"
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default OutboundDetailsPage;
